import {
  AlertCircle,
  ArrowLeft,
  Archive,
  Ban,
  ClipboardList,
  Loader2,
  MoreVertical,
  Pause,
  Play,
  Rocket,
  Ticket,
  FileText,
  Code2,
} from "lucide-react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";
import { toast } from "sonner";
import type { EventStatus, UpdateEventRequest } from "@ticketnjoy/types";

import { DashboardPage } from "@/components/dashboard-page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { QueryError } from "@/components/query-error";
import { isNotFoundError } from "@/lib/api-client";
import {
  useCancelEventMutation,
  useCancelPreviewQuery,
  useEventQuery,
  useEventStatusMutation,
  useUpdateEventMutation,
} from "@/features/events/api";
import {
  EventForm,
  type EditEventFormValues,
} from "@/features/events/event-form";
import {
  formatEventDate,
  isoToLocalInput,
  localInputToIso,
} from "@/features/events/date-helpers";
import { TicketTypesManager } from "@/features/events/ticket-types-manager";
import { EventEmbedPanel } from "@/features/events/event-embed-panel";
import { CancellationRefundsCard } from "@/features/events/cancellation-refunds-card";
import { useConfirm } from "@/lib/confirm";
import { useFormat } from "@/lib/format";
import { useT } from "@/lib/i18n";

type EventTab = "details" | "tickets" | "embed";
const VALID_TABS: readonly EventTab[] = ["details", "tickets", "embed"] as const;

const STATUS_VARIANTS: Record<
  EventStatus,
  "success" | "warning" | "secondary" | "destructive" | "outline" | "default"
> = {
  Draft: "outline",
  Published: "success",
  Paused: "warning",
  Cancelled: "destructive",
  Ended: "secondary",
  Archived: "secondary",
};

export function EventDetailPage() {
  const params = useParams<{ slug: string }>();
  const slug = params.slug ?? "";
  const { data: evt, isLoading, error, refetch, isRefetching } = useEventQuery(slug);
  const t = useT("eventDetail");
  const tStatus = useT("eventStatus");
  const fmt = useFormat();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const activeTab: EventTab = VALID_TABS.includes(tabParam as EventTab)
    ? (tabParam as EventTab)
    : "details";

  const handleTabChange = (value: string) => {
    const next = new URLSearchParams(searchParams.toString());
    if (value === "details") next.delete("tab");
    else next.set("tab", value);
    const query = next.toString();
    navigate(`/events/${slug}${query ? `?${query}` : ""}`, { replace: true });
  };

  if (isLoading) {
    return (
      <DashboardPage>
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardPage>
    );
  }

  if (error && !isNotFoundError(error)) {
    return (
      <DashboardPage>
        <QueryError
          message={t("loadError")}
          onRetry={() => refetch()}
          retrying={isRefetching}
        />
      </DashboardPage>
    );
  }

  if (error || !evt) {
    return (
      <DashboardPage>
        <Card>
          <CardHeader className="items-center text-center">
            <AlertCircle className="h-10 w-10 text-destructive" />
            <CardTitle>{t("notFoundTitle")}</CardTitle>
            <CardDescription>{t("notFoundDescription")}</CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Button asChild>
              <Link to="/events">{t("back")}</Link>
            </Button>
          </CardContent>
        </Card>
      </DashboardPage>
    );
  }

  const editable = !["Cancelled", "Archived"].includes(evt.status);
  const ticketCount = evt.ticketTypes.length;

  return (
    <DashboardPage>
      {/* Slim breadcrumb — tek satır, minimal */}
      <Link
        to="/events"
        className="mb-2 inline-flex items-center text-xs text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="me-1 h-3.5 w-3.5 rtl:-scale-x-100" /> {t("back")}
      </Link>

      {/* Kompakt başlık bloğu: başlık + status badge + kebab menü tek satırda */}
      <div className="mb-1 flex items-start gap-2">
        <h1 className="flex-1 text-xl font-semibold leading-tight sm:text-2xl">
          {evt.title}
        </h1>
        <Badge
          variant={STATUS_VARIANTS[evt.status]}
          className="mt-0.5 shrink-0"
        >
          {tStatus(evt.status)}
        </Badge>
        <StatusMenu slug={slug} status={evt.status} />
      </div>

      <p className="text-xs text-muted-foreground">
        <span className="font-mono" dir="ltr">/events/{evt.slug}</span>
        <span className="mx-1.5">·</span>
        {formatEventDate(evt.startsAt, fmt)} — {formatEventDate(evt.endsAt, fmt)}
      </p>

      {evt.status === "Cancelled" && evt.cancellationReason && (
        <Card className="mt-4 border-destructive/50 bg-destructive/5">
          <CardHeader className="py-3">
            <CardTitle className="text-sm">{t("cancelReason")}</CardTitle>
            <CardDescription className="text-xs">{evt.cancellationReason}</CardDescription>
          </CardHeader>
        </Card>
      )}

      {evt.status === "Cancelled" && <CancellationRefundsCard slug={slug} />}

      {evt.status === "Ended" && (
        <Card className="mt-4 border-primary/30 bg-primary/5">
          <CardContent className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-primary" />
              <div className="text-sm">
                <span className="font-medium">{t("endedTitle")}</span>
                <span className="ms-1 text-xs text-muted-foreground">
                  {t("endedDescription")}
                </span>
              </div>
            </div>
            <Button asChild variant="default" size="sm">
              <Link to={`/reports/events/${evt.id}`}>{t("viewReport")}</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={handleTabChange} className="mt-4">
        <TabsList>
          <TabsTrigger value="details">
            <FileText className="me-1.5 h-4 w-4" />
            {t("tabs.details")}
          </TabsTrigger>
          <TabsTrigger value="tickets">
            <Ticket className="me-1.5 h-4 w-4" />
            {t("tabs.tickets")}
            {ticketCount > 0 ? (
              <span className="ms-1.5 rounded-full bg-muted-foreground/15 px-1.5 py-0.5 text-xs font-normal">
                {ticketCount}
              </span>
            ) : null}
          </TabsTrigger>
          <TabsTrigger value="embed">
            <Code2 className="me-1.5 h-4 w-4" />
            {t("tabs.embed")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="details">
          <Card>
            <CardContent className="pt-5">
              {!editable ? (
                <p className="mb-3 text-xs text-muted-foreground">{t("readOnly")}</p>
              ) : null}
              {editable ? (
                <EditEventBlock slug={slug} evt={evt} />
              ) : (
                <ReadOnlyMeta evt={evt} />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tickets">
          <Card>
            <CardContent className="pt-5">
              <TicketTypesManager
                eventSlug={slug}
                ticketTypes={evt.ticketTypes}
                eventDefaultTaxRate={evt.defaultTaxRate}
                disabled={!editable}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="embed">
          <Card>
            <CardContent className="pt-5">
              {editable ? (
                <EventEmbedPanel slug={slug} evt={evt} />
              ) : (
                <p className="text-sm text-muted-foreground">{t("embedLocked")}</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </DashboardPage>
  );
}

// ---- Sub components ----

/**
 * Kebab (⋮) menüsüyle durum aksiyonları: Yayınla / Duraklat / Yayına Al / İptal Et / Arşivle.
 * Kompakt mobile-first tasarım — başlık satırına inline oturur. İptal akışı için
 * ayrı inline panel açılır (reason textarea + iade özeti + onay). Arşivleme için native confirm.
 *
 * Dropdown davranışı `locale-switcher.tsx` ile aynı pattern: outside click + Escape.
 */
function StatusMenu({ slug, status }: { slug: string; status: EventStatus }) {
  const mutation = useEventStatusMutation(slug);
  const cancelMutation = useCancelEventMutation(slug);
  const confirm = useConfirm();
  const tConfirm = useT("confirm.archiveEvent");
  const t = useT("eventDetail");
  const fmt = useFormat();
  const [open, setOpen] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const preview = useCancelPreviewQuery(slug, showCancel);
  const cancelBlocked = preview.data?.canCancel === false;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const run = (action: "publish" | "pause" | "resume" | "archive", successMsg: string) => {
    setOpen(false);
    mutation.mutate(action, {
      onSuccess: () => toast.success(successMsg),
      onError: (err) => toast.error(err.message),
    });
  };

  const handleCancelSubmit = () => {
    cancelMutation.mutate(
      { reason: cancelReason || null },
      {
        onSuccess: () => {
          toast.success(t("actions.cancelled"));
          setShowCancel(false);
          setCancelReason("");
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  // Terminal durumlar: aksiyon yok → menü gösterme
  if (status === "Archived" || status === "Cancelled") {
    return null;
  }

  interface MenuAction {
    label: string;
    icon: typeof Rocket;
    onClick: () => void;
    destructive?: boolean;
  }

  const actions: MenuAction[] = [];
  if (status === "Draft") {
    actions.push({
      label: t("actions.publish"),
      icon: Rocket,
      onClick: () => run("publish", t("actions.published")),
    });
  }
  if (status === "Published") {
    actions.push({
      label: t("actions.pause"),
      icon: Pause,
      onClick: () => run("pause", t("actions.paused")),
    });
  }
  if (status === "Paused") {
    actions.push({
      label: t("actions.resume"),
      icon: Play,
      onClick: () => run("resume", t("actions.resumed")),
    });
  }
  if (status === "Published" || status === "Paused") {
    actions.push({
      label: t("actions.cancel"),
      icon: Ban,
      onClick: () => {
        setOpen(false);
        setShowCancel(true);
      },
      destructive: true,
    });
  }
  actions.push({
    label: t("actions.archive"),
    icon: Archive,
    onClick: async () => {
      setOpen(false);
      const ok = await confirm({
        title: tConfirm("title"),
        description: tConfirm("description"),
        confirmLabel: tConfirm("action"),
        destructive: true,
      });
      if (ok) run("archive", t("actions.archived"));
    },
  });

  return (
    <>
      <div ref={rootRef} className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          disabled={mutation.isPending}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={t("actionsLabel")}
          className={cn(
            "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border bg-background text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground",
            mutation.isPending && "opacity-60",
          )}
        >
          {mutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <MoreVertical className="h-4 w-4" />
          )}
        </button>

        {open && (
          <div
            role="menu"
            className="absolute end-0 top-full z-50 mt-1 min-w-[10rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg"
          >
            {actions.map((a) => {
              const Icon = a.icon;
              return (
                <button
                  key={a.label}
                  role="menuitem"
                  type="button"
                  onClick={a.onClick}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors",
                    a.destructive
                      ? "text-destructive hover:bg-destructive/10"
                      : "hover:bg-accent hover:text-accent-foreground",
                  )}
                >
                  <Icon className="h-4 w-4" />
                  <span className="flex-1 text-start">{a.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {showCancel && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="cancel-event-title"
            className="w-full max-w-md space-y-3 rounded-lg border bg-background p-4 shadow-xl"
          >
            <div>
              <h3 id="cancel-event-title" className="text-base font-semibold">
                {t("cancelDialog.title")}
              </h3>
              <p className="mt-1 text-xs text-muted-foreground">{t("cancelDialog.description")}</p>
            </div>
            <Textarea
              rows={3}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder={t("cancelDialog.placeholder")}
            />
            <div className="space-y-1 rounded-md border bg-muted/40 p-3 text-xs">
              {preview.isLoading ? (
                <p className="flex items-center text-muted-foreground">
                  <Loader2 className="me-1 h-3.5 w-3.5 animate-spin" />
                  {t("cancelDialog.previewLoading")}
                </p>
              ) : preview.error ? (
                <p className="text-muted-foreground">{t("cancelDialog.previewError")}</p>
              ) : preview.data && preview.data.orderCount > 0 ? (
                <>
                  <p className="font-medium">
                    {t("cancelDialog.refundSummary", {
                      orders: fmt.number(preview.data.orderCount),
                      tickets: fmt.number(preview.data.ticketCount),
                      amount: fmt.money(preview.data.refundAmount, preview.data.currency),
                    })}
                  </p>
                  <p className={cancelBlocked ? "text-destructive" : "text-muted-foreground"}>
                    {t(cancelBlocked ? "cancelDialog.noPermission" : "cancelDialog.refundNote")}
                  </p>
                </>
              ) : (
                <p className="text-muted-foreground">{t("cancelDialog.noRefunds")}</p>
              )}
            </div>
            {cancelMutation.isPending && (
              <p className="text-xs text-muted-foreground">{t("cancelDialog.processing")}</p>
            )}
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCancel(false)}
                disabled={cancelMutation.isPending}
              >
                {t("cancelDialog.dismiss")}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleCancelSubmit}
                disabled={cancelMutation.isPending || preview.isLoading || cancelBlocked}
              >
                {cancelMutation.isPending && (
                  <Loader2 className="me-1 h-4 w-4 animate-spin" />
                )}
                {t("cancelDialog.submit")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function EditEventBlock({
  slug,
  evt,
}: {
  slug: string;
  evt: {
    id: string;
    title: string;
    description: string | null;
    coverImageUrl: string | null;
    venueName: string | null;
    venueAddress: string | null;
    venueCity: string | null;
    latitude: number | null;
    longitude: number | null;
    startsAt: string;
    endsAt: string;
    defaultTaxRate: number;
    embedEnabled: boolean;
    embedSlug: string | null;
    embedMode: "Compact" | "Inline";
  };
}) {
  const mutation = useUpdateEventMutation(slug);
  const t = useT("eventDetail");
  const tCommon = useT("common");

  const onSubmit = async (values: EditEventFormValues) => {
    // Backend PUT full update — Embed sekmesinde ayarlanmış alanları
    // ezmemek için mevcut event verisinden okuyup payload'a dahil
    // ediyoruz.
    const payload: UpdateEventRequest = {
      title: values.title,
      description: values.description || null,
      coverImageUrl: values.coverImageUrl || null,
      venueName: values.venueName || null,
      venueAddress: values.venueAddress || null,
      venueCity: values.venueCity || null,
      latitude: values.latitude ?? null,
      longitude: values.longitude ?? null,
      startsAt: localInputToIso(values.startsAt)!,
      endsAt: localInputToIso(values.endsAt)!,
      defaultTaxRate: values.defaultTaxRate,
      embedEnabled: evt.embedEnabled,
      embedSlug: evt.embedSlug,
      embedMode: evt.embedMode,
    };

    try {
      await mutation.mutateAsync(payload);
      toast.success(t("saved"));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : tCommon("saveFailed"));
    }
  };

  return (
    <EventForm
      mode="edit"
      eventId={evt.id}
      isSubmitting={mutation.isPending}
      defaultValues={{
        title: evt.title,
        description: evt.description ?? "",
        coverImageUrl: evt.coverImageUrl ?? "",
        venueName: evt.venueName ?? "",
        venueAddress: evt.venueAddress ?? "",
        venueCity: evt.venueCity ?? "",
        latitude: evt.latitude,
        longitude: evt.longitude,
        startsAt: isoToLocalInput(evt.startsAt),
        endsAt: isoToLocalInput(evt.endsAt),
        defaultTaxRate: evt.defaultTaxRate,
      }}
      onSubmit={onSubmit}
    />
  );
}

function ReadOnlyMeta({ evt }: { evt: { title: string; venueName: string | null; startsAt: string; endsAt: string } }) {
  const t = useT("eventDetail.fields");
  const fmt = useFormat();
  return (
    <dl className="grid gap-3 text-sm sm:grid-cols-2">
      <Row label={t("title")} value={evt.title} />
      <Row label={t("venue")} value={evt.venueName ?? "—"} />
      <Row label={t("startsAt")} value={formatEventDate(evt.startsAt, fmt)} />
      <Row label={t("endsAt")} value={formatEventDate(evt.endsAt, fmt)} />
    </dl>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}
